import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter } from '~src/authentication/components/userIdentitiesSpace/SiteUsers/AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter';
import { BackgroundJobsSpaceProvider } from '~src/backgroundJob/components/BackgroundJobsSpaceProvider';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { ConnectedReduxRouterErrorWrapper } from '~src/bootstrap/routerHelpers/ReduxRouterErrorWrapper';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { SiteBroadcastsSpaceProvider } from '~src/broadcasts/space/SiteBroadcastsSpaceProvider';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { AllLabelsSpaceProvider } from '~src/catalog/components/labelValues/AllLabelsSpaceProvider';
import { ConnectedCatalogBrowserHost } from '~src/catalog/ConnectedCatalogBrowserHost';
import { CollectionsSpaceRouter } from '~src/Collections/space/CollectionsSpaceRouter';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { DataRoleSpaceRouter } from '~src/dataRole/components/DataRoleSpaceRouter';
import { RedirectToFavoritedDataRoleProvider } from '~src/dataRole/components/RedirectToFavoritedDataRoleProvider';
import { DatasourceSpaceRouter } from '~src/datasource/components/DatasourceSpaceRouter';
import { ExploreDatasourceProvider } from '~src/datasource/components/ExploreDatasourceProvider';
import { RedirectToFavoritedDatasourceProvider } from '~src/datasource/components/RedirectToFavoritedDatasourceProvider';
import { AddUserToDataAlertProvider, RemoveUserFromDataAlertProvider } from '~src/dda/components/AddOrRemoveFromDataAlertProvider';
import { ConnectedFavoritesSpace } from '~src/favorites/space/ConnectedFavoritesSpace';
import { FlowSpaceFavoriteRedirect } from '~src/flow/components/FlowSpaceFavoriteRedirect';
import { FlowSpaceRouter } from '~src/flow/components/FlowSpaceRouter';
import { HomeSpaceRouter } from '~src/home/components/HomeSpaceRouter';
import { LensSpaceRouter } from '~src/lens/LensSpaceRouter';
import { IsNlpEnabled } from '~src/lens/utils/LensUtil';
import { ConnectedMetricDetailSpace, isMetricOwner, renderMetricDetailSpaceContent, showResumeMetricWarning, showSuspendedMetricWarning, userCanResumeMetric } from '~src/metrics/components/MetricsSpace/MetricDetailSpace';
import { RedirectToFavoritedMetricProvider } from '~src/metrics/components/MetricsSpace/RedirectToFavoritedMetricProvider';
import { isMetricsEnabled, isMetricsRequestAccessDialogEnabled } from '~src/metrics/util/MetricUtils';
import { PersonalSpaceProvider } from '~src/personalSpace/components/spaceProviders/PersonalSpaceProvider';
import { PredictiveModelSpaceRouter } from '~src/predictiveModel/components/PredictiveModelSpaceRouter';
import { ProjectSpaceRouter } from '~src/projects/components/ProjectSpaceRouter';
import { RedirectToFavoritedProjectProvider } from '~src/projects/components/RedirectToFavoritedProjectProvider';
import { Projects } from '~src/projects/resources/Projects';
import { PublishedConnectionSpaceFavoriteRedirect } from '~src/publishedConnection/components/PublishedConnectionSpaceFavoriteRedirect';
import { PublishedConnectionSpaceRouter } from '~src/publishedConnection/components/PublishedConnectionSpaceRouter';
import { PublishedConnectionTableSpaceFavoriteRedirect } from '~src/publishedConnectionTable/components/PublishedConnectionTableSpaceFavoriteRedirect';
import { PublishedConnectionTableSpaceRouter } from '~src/publishedConnectionTable/components/PublishedConnectionTableSpaceRouter';
import { RecentsSpaceProvider } from '~src/recents/space/RecentsSpaceProvider';
import { RecommendationsSpaceProvider } from '~src/recommendations/components/spaces/RecommendationsSpaceProvider';
import { AllSchedulesSpaceProvider } from '~src/schedule/components/AllSchedulesSpaceProvider';
import { ScheduleSpaceRouter } from '~src/schedule/components/ScheduleRouter';
import { ConnectedSearchMicroViewHost } from '~src/search/components/SearchMicroViewHost';
import { ConnectedSearchMicroViewHostWithGenericPackage } from '~src/search/components/SearchMicroviewHostV2';
import { SearchSpaceRouter } from '~src/search/components/SearchSpaceRouter';
import { SiteStatusSpaceProvider } from '~src/serverStatus/components/siteStatusSpace/SiteStatusSpaceProvider';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { IntegrationsSubspaceName } from '~src/settings/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { SharedWithMeSpaceProvider } from '~src/sharing/components/sharedWithMe/SharedWithMeSpaceProvider';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '~src/spacesPlatform/components/AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { selectPreviousUrl } from '~src/spacesPlatform/selectors/NavigationStateSelectors';
import { SiteSpaceContentType } from '~src/spacesPlatform/state/SiteSpaceContentTypeState';
import { SiteSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { StartersSpaceProvider } from '~src/starters/components/StartersSpaceProvider';
import { SiteTasksSpaceRouter } from '~src/tasks/components/siteTasksSpace/SiteTasksSpaceRouter';
import { AllGroupsSpaceProvider } from '~src/userManagement/groups/components/space/AllGroupsSpaceProvider';
import { GroupSpaceRouter } from '~src/userManagement/groups/components/space/GroupSpaceRouter';
import { SiteUserSpaceRouter } from '~src/userManagement/users/components/siteUserSpace/SiteUserSpaceRouter';
import { AllSiteUsersSpaceProvider } from '~src/userManagement/users/components/siteUsersSpace/AllSiteUsersSpaceProvider';
import { ConnectedRedirectToViewSpaceProvider, RedirectViewType } from '~src/viz/components/RedirectToViewProvider';
import { VizSpaceUrlUpdater } from '~src/viz/components/VizSpaceUrlUpdater';
import { VizUrl } from '~src/viz/helpers/VizUrlHelpers';
import { RedirectToFavoritedWorkbookProvider } from '~src/workbook/components/RedirectToFavoritedWorkbookProvider';
import { WorkbookSpaceRouter } from '~src/workbook/components/WorkbookSpaceRouter';
import { Workbooks } from '~src/workbook/resources/Workbooks';

import { ConnectedSiteSettingsSpaceProvider as SiteSettingsSpaceProvider } from '../../settings/components/subspaces/SiteSettingsSpace/SiteSettingsSpaceProvider';
import { SiteSpaceProvider, SiteSpaceProviderProps } from '../components/siteSpace/SiteSpaceProvider';

export interface SiteSpaceRouterOwnProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface SiteSpaceRouterStateProps {
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
}

export type SiteSpaceRouterComponentProps = SiteSpaceRouterOwnProps & SiteSpaceRouterStateProps;

export class SiteSpaceRouterComponent extends React.PureComponent<SiteSpaceRouterComponentProps> {
  private server: SessionManagementService;

  public constructor(props: SiteSpaceRouterComponentProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <Switch>
        <Route path={this.props.urls.searchPath()} render={() => (FeatureFlags.genericMicroview ? <ConnectedSearchMicroViewHostWithGenericPackage /> : FeatureFlags.searchV2
          ? <ConnectedSearchMicroViewHost />
          : <SearchSpaceRouter {...this.props} />)} />
        <Route exact path={this.props.urls.dataRolesPath()} render={this.siteSpaceprovider('dataRole')} />
        <Route exact path={this.props.urls.projectsPath()} render={this.siteSpaceprovider('project')} />
        <Route exact path={this.props.urls.explorePath()} render={this.siteSpaceprovider('explore')} />
        {this.server.flowsOnSiteEnabled() &&
          <Route exact path={this.props.urls.flowsPath()} render={this.siteSpaceprovider('flow')} />
        }
        {FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.virtualConnectionsPath()} render={this.siteSpaceprovider('virtualConnection')} />
        }
        {
          IsNlpEnabled() &&
          <Route exact path={this.props.urls.lensesPath()} render={this.siteSpaceprovider('lens')} />
        }
        {FeatureFlags.prometheusPredictiveModelEnabled &&
          <Route exact path={this.props.urls.predictiveModelsPath()} render={this.siteSpaceprovider('predictiveModel')} />
        }
        <Route exact path={this.props.urls.viewsPath()} render={this.siteSpaceprovider('view')} />
        {
          isMetricsEnabled() &&
          <Route exact path={this.props.urls.metricsPath()} render={this.siteSpaceprovider('metric')} />
        }

        {FeatureFlags.requestAccessDialogEnabled &&
          <Route path={this.props.urls.viewGrantAccessPath(':workbookName', ':viewName')} render={({ match }) =>
            <Redirect to={this.props.urls.viewPath(`${match.params.workbookName}/${match.params.viewName}`)} />
          } />
        }
        {this.getVizRoute()}
        <Route path={this.props.urls.redirectToFavoriteViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'favorite')} />
        <Route path={this.props.urls.redirectToViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'regular')} />
        <Route path={this.props.urls.redirectToCustomViewPathWithLuid(':viewId', ':customViewLuid', ':customViewUrlId')} render={({ match }) => this.redirectToViewProvider(match.params.viewId, 'customView', undefined, match.params.customViewUrlId, match.params.customViewLuid)} />

        {FeatureFlags.authorMetricsEnabled && FeatureFlags.metricsSpaceEnabled && this.getWorkbookUsageMetricRoute()}
        <Route exact path={this.props.urls.workbooksPath()} render={this.siteSpaceprovider('workbook')} />
        <Route path={this.props.urls.workbookPath(':id')} render={() => <WorkbookSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToWorkbookPath(':id')} render={({ match }) => this.redirectToFavoritedWorkbookProvider(match.params.id)} />

        {this.getAllGroupsRoute(this.server)}

        {CollectionUtils.checkCollectionSiteSetting() && this.getCollectionsRoute()}

        {FeatureFlags.broadcastUI && FeatureFlags.broadcastEnabled && this.getBroadcastsRoute()}

        <Route exact path={this.props.urls.datasourcesPath()} render={this.siteSpaceprovider('datasource')} />
        <Route path={this.props.urls.datasourcePath(':id')} render={() => <DatasourceSpaceRouter {...this.props} />} />
        {/* // explore/datasource navigates to the data source by its Published Datasource ID, rather than the Unified Datasource (connection) ID */}
        <Route exact path={this.props.urls.exploreDatasourcePath(':id')} render={({ match }) => this.exploreDatasourceProvider(match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourcePath(':id', ':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting, match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourceDoesntExistPath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting)} />

        <Route path={this.props.urls.flowPath(':id')} render={() => <FlowSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToFlowPath(':id')} render={({ match }) => this.redirectToFlowProvider(match.params.id)} />

        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route path={this.props.urls.virtualConnectionPath(':id')} render={() => <PublishedConnectionSpaceRouter {...this.props} />} />
        }
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.redirectToVirtualConnectionPath(':id')} render={({ match }) => this.redirectToPublishedConnectionProvider(match.params.id)} />
        }

        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route path={this.props.urls.virtualConnectionTablePath(':id')} render={() => <PublishedConnectionTableSpaceRouter {...this.props} />} />
        }
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.redirectToVirtualConnectionTablePath(':id')} render={({ match }) => this.redirectToPublishedConnectionTableProvider(match.params.id)} />
        }

        {isMetricsEnabled() && isMetricsRequestAccessDialogEnabled() &&
          <Route path={this.props.urls.metricGrantAccessPath(':id')} render={({ match }) =>
            <Redirect to={this.props.urls.redirectToMetricPath(match.params.id)} />
          } />
        }
        {isMetricsEnabled() &&
          <Route path={this.props.urls.metricPath(':id')} render={({ match }) =>
            <ConnectedMetricDetailSpace
              siteName={this.props.siteName}
              id={match.params.id}
              urls={this.props.urls}
              renderContent={renderMetricDetailSpaceContent}
              showResumeMetricWarning={showResumeMetricWarning}
              showSuspendedMetricWarning={showSuspendedMetricWarning}
              userCanResumeMetric={userCanResumeMetric}
              isMetricOwner={isMetricOwner}
              routeLocationSearch={this.props.routeLocationSearch}
              projects={Projects}
              grantAccessDisplay={FeatureFlags.requestAccessDialogEnabled && this.props.previousUrl.includes(this.props.urls.metricGrantAccessPath(match.params.id))}
              grantAccessUrl={this.props.previousUrl}
            />
          } />
        }
        {isMetricsEnabled() &&
          <Route path={this.props.urls.redirectToMetricPath(':id')} render={({ match }) =>
            <RedirectToFavoritedMetricProvider metricId={match.params.id} urls={this.props.urls} />
          } />
        }
        {this.getUsersRoute(this.server)}
        <Route path={this.props.urls.baseUserPath()} render={() => <SiteUserSpaceRouter {...this.props} />} />

        <Route path={this.props.urls.projectPath(':id')} render={() => <ProjectSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToProjectPath(':id')} render={({ match }) => this.redirectToFavoritedProjectProvider(match.params.id)} />

        <Route path={this.props.urls.favoritesPath()} render={this.favoritesSpace} />
        <Route path={this.props.urls.recentsPath()} render={this.recentsSpace} />
        {FeatureFlags.homePage &&
          <Route path={this.props.urls.homePath()} render={() => <HomeSpaceRouter {...this.props} />} />
        }
        <Route path={this.props.urls.recommendationListPath()} render={() => <RecommendationsSpaceProvider {...this.props} />} />

        <Route path={this.props.urls.dataRolePath(':id')} render={() => <DataRoleSpaceRouter {...this.props} />} />
        <Route path={this.props.urls.redirectToDataRolePath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDataRoleProvider(match.params.idForFavoriting)} />
        <Route path={this.props.urls.lensPath(':luid')} render={() => <LensSpaceRouter {...this.props} />} />
        {
          FeatureFlags.prometheusPredictiveModelEnabled &&
          <Route path={this.props.urls.predictiveModelPath(':id')} render={() => <PredictiveModelSpaceRouter {...this.props} />} />
        }
        {this.getSiteStatusRoute(this.server)}
        {this.getSiteStatusVizRoute(this.server)}

        {this.getSiteTasksRoute(this.server)}
        {FeatureFlags.labelManagementEnabled && this.getLabelsRoute(this.server)}
        {
          FeatureFlags.jobManagementEnabled &&
          this.getSiteBackgroundJobsRoute(this.server)
        }
        {
          this.getDataCatalogRoute(this.server)
        }
        <Route path={this.props.urls.addUserToDataAlertPath(':dataAlertId')} render={({ match }) => <AddUserToDataAlertProvider dataAlertId={match.params.dataAlertId} />} />
        <Route path={this.props.urls.removeUserFromDataAlertPath(':dataAlertId')} render={({ match }) => <RemoveUserFromDataAlertProvider dataAlertId={match.params.dataAlertId} />} />

        {this.getGroupRoute(this.server)}
        {this.getScheduleRoute(this.server)}
        {this.getAllSchedulesRoute(this.server)}
        {this.settingsRoute(this.props.urls.settingsPath(), 'settings')}
        {FeatureFlags.extensionsSettingsTabEnabled && this.settingsRoute(this.props.urls.settingsExtensionsPath(), 'extensions')}
        {
          ServerSettingsUnauthenticated.isSiteSamlEnabled() &&
          this.settingsRoute(this.props.urls.settingsAuthenticationPath(), 'authentication')
        }
        {
          ServerSettingsUnauthenticated.isBridgeEnabled() &&
          this.settingsRoute(this.props.urls.settingsBridgePath(), 'bridge')
        }
        {
          FeatureFlags.connectedApplicationsSettingsTabEnabled &&
          this.settingsRoute(this.props.urls.settingsConnectedApplicationsPath(), 'connectedApplications')
        }
        {
          // Needed since the connected applications settings subspace has an additional nested subspace
          FeatureFlags.connectedApplicationsSettingsTabEnabled &&
          this.settingsRoute(this.props.urls.connectedApplicationDirectTrustPath(':id'), 'connectedApplications')
        }
        {
          FeatureFlags.connectedApplicationsSettingsTabEnabled && FeatureFlags.siteLevelEASEnabled &&
          this.settingsRoute(this.props.urls.connectedApplicationEasPath(':id'), 'connectedApplications')
        }
        {
          FeatureFlags.mobileSecuritySettingsEnabled &&
          this.settingsRoute(this.props.urls.settingsMobilePath(), MobileSettingsSubspaceName as SiteSettingsSubspaceName)
        }
        {FeatureFlags.startersChannelEnabled && this.getStartersRouteByConnectionType()}
        {this.server.isNamedSharingEnabled() && this.getSharedWithMeRoute()}
        {this.getPersonalSpaceRoute(this.server)}
        {this.settingsRoute(this.props.urls.settingsIntegrationsPath(), IntegrationsSubspaceName as SiteSettingsSubspaceName)}
        {
          // TFSID 1474547: The Prometheus project was put on hold in October 2022.
          // To resume the project, revert back this commit.
          // {FeatureFlags.prometheusPredictiveModelEnabled && this.getPredictiveModelEditorRoute()}
        }
        <Route render={this.error} />
      </Switch>
    );
  }

  private settingsRoute(path: string, subspaceName: SiteSettingsSubspaceName) {
    // isSiteAdminSettingsEnabled is set on the backend (true for online and false for on-prem),
    // so that site admin can access settings only in online environment.
    if (this.server.isSessionServerAdmin()
      || (this.server.isSessionSiteAdmin() && this.server.isSiteAdminSettingsEnabled())) {
      return <Route exact path={path} render={this.settingsProvider(subspaceName)} />;
    }
  }

  private settingsProvider(subspaceName: SiteSettingsSubspaceName) {
    return () =>
      <SiteSettingsSpaceProvider
        urls={this.props.urls}
        siteName={this.props.siteName}
        subspaceName={subspaceName}
      />;
  }

  private getDataCatalogRoute(server: SessionManagementService) {
    return server.catalogingOnSiteEnabled() &&
      <Route path={this.props.urls.catalogPath()}
        render={() => <ConnectedCatalogBrowserHost />}
      />;
  }

  private getSiteBackgroundJobsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.jobsPath()}
        render={(routeProps) => <BackgroundJobsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
  }

  private getAllSchedulesRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.schedulesPath()}
        render={(routeProps) => <AllSchedulesSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getBroadcastsRoute() {
    return <Route
      path={this.props.urls.broadcastsPath()}
      render={(props: RouteComponentProps) =>
        <SiteBroadcastsSpaceProvider
          siteName={this.props.siteName}
          routeLocationPath={props.location.pathname}
          routeLocationSearch={props.location.search} />}
    />;
  }

  private getCollectionsRoute() {
    return <Route
      path={this.props.urls.collectionsPath()}
      render={() =>
        <CollectionsSpaceRouter
          urls={this.props.urls}
          siteName={this.props.siteName}
          previousUrl={this.props.previousUrl}
        />}
    />;
  }

  private getWorkbookUsageMetricRoute() {
    return <Route
      exact path={this.props.urls.workbookUsageMetricPath({ workbookId: ':workbookId', usageMetricId: ':usageMetricId' })}
      render={({ match }) =>
        <ConnectedMetricDetailSpace
          urls={this.props.urls}
          siteName={this.props.siteName}
          id={match.params.usageMetricId}
          routeLocationSearch={this.props.routeLocationSearch}
          renderContent={renderMetricDetailSpaceContent}
          showResumeMetricWarning={_.noop}
          showSuspendedMetricWarning={_.noop}
          userCanResumeMetric={() => false}
          isMetricOwner={() => false}
          projects={Projects}
          workbooks={Workbooks}
          workbookId={match.params.workbookId}
          grantAccessDisplay={false}
          grantAccessUrl={this.props.previousUrl}
        />
      }
    />;
  }

  private getAllGroupsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.groupsPath()}
        render={(routeProps) => <AllGroupsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getScheduleRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.schedulePath(':id')} render={() => <ScheduleSpaceRouter {...this.props} />} />;
  }

  private getGroupRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.groupPath(':id')} render={() => <GroupSpaceRouter {...this.props} />} />;
  }

  private siteSpaceprovider = (siteSpaceContentType: SiteSpaceContentType): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const siteSpaceProviderProps: SiteSpaceProviderProps = {
        siteName: this.props.siteName,
        siteSpaceContentType,
        routeLocationSearch: props.location.search,
        routeLocationPath: props.location.pathname,
        urls: this.props.urls,
      };

      return <SiteSpaceProvider {...siteSpaceProviderProps} key={'1'} />;
    };
  };

  private getSiteStatusVizRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.siteStatusVizPath(':sheetUrlId')}
        render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
          sheetUrlId={routeProps.match.params.sheetUrlId}
          routeLocationSearch={routeProps.location.search}
          history={routeProps.history}
          urls={this.props.urls}
          isServerAnalysis={false}
          renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
        />}
      />;
  }

  private getVizRoute() {
    return <Route path={this.props.urls.viewPath(':viewPath')}
      render={(routeProps) => {
        return (<VizSpaceUrlUpdater.ConnectedContainer
          vizPathFromUrl={VizUrl.getVizPathFromUrlLocation(routeProps.location)}
          routeLocationSearch={routeProps.location.search}
          urls={this.props.urls}
          renderProvider={VizSpaceUrlUpdater.renderProvider}
          grantAccessDisplay={this.isGrantAccessDisplayEnabled(this.props, routeProps.location.pathname)}
          grantAccessUrl={this.props.previousUrl}
        />);
      }}
    />;
  }

  private isGrantAccessDisplayEnabled(props: SiteSpaceRouterComponentProps, pathName: string) {
    // For grant access view scenarios where the url is redirected from the grant access view url to the 
    // actual view url, grant access dialog should be displayed on the UI.  We need to extract the 
    // <WorkbookName> and <ViewName> from the pathName to figure out this. For default sites the pathName 
    // looks like this "/views/<WorkbookName>/<ViewName>" and for custom sites the pathName looks like 
    // this "/site/<SiteName>/views/<WorkbookName>/<ViewName>".
    const length = pathName.split('/').length;
    if (FeatureFlags.requestAccessDialogEnabled && (pathName && length > 1)) {
      return (props.previousUrl.includes(props.urls.viewGrantAccessPath(pathName.split('/')[length - 2], pathName.split('/')[length - 1])));
    }
  }

  private favoritesSpace = (props: RouteComponentProps): JSX.Element => (
    <ConnectedFavoritesSpace
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );

  private recentsSpace = (props: RouteComponentProps): JSX.Element => (
    <RecentsSpaceProvider
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );


  private exploreDatasourceProvider = (publishedDatasourceId: string): JSX.Element => (
    <ExploreDatasourceProvider publishedDatasourceId={publishedDatasourceId} urls={this.props.urls} />
  );

  private redirectToFavoritedDataRoleProvider = (roleId?: string): JSX.Element => (
    <RedirectToFavoritedDataRoleProvider roleId={roleId} urls={this.props.urls} />
  );

  private redirectToFavoritedDatasourceProvider = (idForFavoriting: string, datasourceId?: string): JSX.Element => (
    <RedirectToFavoritedDatasourceProvider idForFavoriting={idForFavoriting} datasourceId={datasourceId} urls={this.props.urls} />
  );

  private redirectToFavoritedWorkbookProvider = (workbookId?: string): JSX.Element => (
    <RedirectToFavoritedWorkbookProvider workbookId={workbookId} urls={this.props.urls} />
  );

  private redirectToFavoritedProjectProvider = (projectId?: string): JSX.Element => (
    <RedirectToFavoritedProjectProvider projectId={projectId} urls={this.props.urls} />
  );

  private redirectToViewProvider = (viewId: string, redirectType: RedirectViewType, username?: string, customViewUrlId?: string, customViewLuid?: string): JSX.Element => (
    <ConnectedRedirectToViewSpaceProvider viewId={viewId} customViewUrlId={customViewUrlId} urls={this.props.urls} redirectType={redirectType} customViewLuid={customViewLuid} />
  );

  private redirectToFlowProvider = (flowId: string) => (
    <FlowSpaceFavoriteRedirect flowId={flowId} urls={this.props.urls} />
  );

  private redirectToPublishedConnectionProvider = (publishedConnectionId: string) => (
    <PublishedConnectionSpaceFavoriteRedirect publishedConnectionId={publishedConnectionId} urls={this.props.urls} />
  );

  private redirectToPublishedConnectionTableProvider = (publishedConnectionTableId: string) => (
    <PublishedConnectionTableSpaceFavoriteRedirect publishedConnectionTableId={publishedConnectionTableId} urls={this.props.urls} />
  );

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls} />;
  };

  private getSiteStatusRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route
        path={this.props.urls.statusPath()}
        render={() =>
          <SiteStatusSpaceProvider
            siteName={this.props.siteName}
            urls={this.props.urls}
          />
        }
      />;
  }

  private getLabelsRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.labelsPath()}
        render={(routeProps) =>
          <>
            <CatalogProviders>
              <AllLabelsSpaceProvider
                siteName={this.props.siteName}
                routeLocationSearch={routeProps.location.search}
                routeLocationPath={routeProps.location.pathname}
                urls={this.props.urls}
              />
            </CatalogProviders>
          </>

        }
      />;
  }

  private getUsersRoute(server: SessionManagementService): React.ReactNode {
    if (FeatureFlags.identityMigrationEnabled && server.isSingleSiteOnPremServer() &&
      this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      // For SingleSite OnPrem we split the Users space into two subspaces, Site Users and Identities.
      return <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          siteName={this.props.siteName}
          urls={this.props.urls}
        />}
      />;
    } else {
      return this.server.isUserAuthorized(AuthLevel.SiteAdmin) &&
        <Route path={this.props.urls.usersPath()} render={(routeProps) =>
          <AllSiteUsersSpaceProvider
            routeLocationPath={routeProps.location.pathname}
            routeLocationSearch={routeProps.location.search}
            siteName={this.props.siteName}
            urls={this.props.urls}
          />}
        />;
    }
  }

  private getSharedWithMeRoute(): React.ReactNode {
    return <Route path={this.props.urls.sharedWithMePath()}
      render={(routeProps) => <SharedWithMeSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
      />}
    />;
  }

  private getPersonalSpaceRoute(server: SessionManagementService): React.ReactNode {
    return server.shouldShowPersonalSpace() &&
      <Route path={this.props.urls.personalSpacePath()}
        render={(routeProps) => <PersonalSpaceProvider
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getStartersRouteByConnectionType() {
    return <Route
      exact path={this.props.urls.startersByConnectionTypePath(':starters')}
      render={(routeProps) => <StartersSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
        urls={this.props.urls}
        connectionType={routeProps.match.params.starters}
      />}
    />;
  }

  private getSiteTasksRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.tasksPath()} render={() => <SiteTasksSpaceRouter {...this.props} />} />;
  }
}

const mapStateToProps: MapStateToProps<SiteSpaceRouterStateProps> = (state: AppState) => ({
  previousUrl: selectPreviousUrl(state),
  urls: siteUrlsSelector(state),
  routerReactKey: state.routerSpaceReactKey
});

export const ConnectedSiteSpaceRouter: React.ComponentClass<SiteSpaceRouterOwnProps>
  = connect(mapStateToProps)(SiteSpaceRouterComponent);

export interface SiteSpaceRouterProps extends SiteSpaceRouterOwnProps {
  readonly isBusy: boolean;
}

export class SiteSpaceRouter extends React.Component<SiteSpaceRouterProps> {
  public render() {
    if (this.props.isBusy) {
      // Render a blank page while we in are in middle of switching sites (isBusy===true)
      // In this point the url will contain the new site but we aren't ready to display
      // any data yet.
      return null;
    }

    return (
      <ConnectedReduxRouterErrorWrapper>
        <CatalogProviders>
        <ConnectedSiteSpaceRouter
          siteName={this.props.siteName}
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
        </CatalogProviders>
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
